import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useClientsSearch } from '../hooks/useClientsSearch';
import PaginationControls from '../../../shared/components/ui/PaginationControls';

const PAGE_SIZE = 5;

export default function ClientsPage() {
  const [page, setPage] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({ name: '', surname: '', phone: '', email: '' });
  const { clients, filters, loading, setFilters, searchClients } = useClientsSearch();

  const guardianClients = useMemo(
    () => clients.filter((client) => Array.isArray(client.minors) && client.minors.length > 0),
    [clients]
  );

  function handleChange(event) {
    setFilters((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));
  }

  function handleFilter(event) {
    event.preventDefault();
    setAppliedFilters(filters);
    searchClients({ phone: filters.phone });
    setPage(0);
  }

  function handleClear() {
    const cleared = { name: '', surname: '', phone: '', email: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    searchClients({ phone: '' });
    setPage(0);
  }

  // Frontend filtering for surname and email (backend only supports name + phone)
  const filteredClients = useMemo(() => {
    let result = guardianClients;

    if (appliedFilters.name) {
      const term = appliedFilters.name.toLowerCase();
      result = result.filter((client) => {
        const guardianName = client.fullName || client.name || '';
        const hasMinorMatch = (client.minors || []).some((minor) =>
          (minor.fullName || minor.name || '').toLowerCase().includes(term)
        );

        return guardianName.toLowerCase().includes(term) || hasMinorMatch;
      });
    }

    if (appliedFilters.surname) {
      const term = appliedFilters.surname.toLowerCase();
      result = result.filter((client) =>
        (client.fullSurname || '').toLowerCase().includes(term)
        || (client.minors || []).some((minor) =>
          `${minor.surname || ''} ${minor.secondSurname || ''}`.trim().toLowerCase().includes(term)
        )
      );
    }

    if (appliedFilters.email) {
      const term = appliedFilters.email.toLowerCase();
      result = result.filter((client) =>
        (client.email || '').toLowerCase().includes(term)
      );
    }

    return result;
  }, [appliedFilters.email, appliedFilters.name, appliedFilters.surname, guardianClients]);

  const paginatedClients = filteredClients.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const hasAppliedFilters = Boolean(appliedFilters.name || appliedFilters.surname || appliedFilters.phone || appliedFilters.email);
  const hasPendingFilterChanges = (
    filters.name !== appliedFilters.name
    || filters.surname !== appliedFilters.surname
    || filters.phone !== appliedFilters.phone
    || filters.email !== appliedFilters.email
  );

  useEffect(() => {
    setPage((previous) => Math.min(previous, totalPages - 1));
  }, [totalPages]);

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] shadow-[0_24px_60px_rgba(15,23,42,0.06)] p-8 backdrop-blur-xl"
      >
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.32em] text-slate-400">Clientes</p>
          <h1 className="mt-3 text-[54px] leading-[0.96] font-semibold tracking-[-0.06em] text-slate-950">Registro de menores</h1>
          <p className="mt-4 text-lg text-slate-500">
            Consulta rápida de tutores registrados y sus menores a cargo. Filtra por nombre o apellidos del tutor o del menor, teléfono o email.
          </p>
        </div>

        <form onSubmit={handleFilter} className="mt-8 space-y-4">
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <input
              type="text"
              name="name"
              value={filters.name}
              onChange={handleChange}
              placeholder="Nombre tutor o menor"
              className="app-input"
            />
            <input
              type="text"
              name="surname"
              value={filters.surname}
              onChange={handleChange}
              placeholder="Apellidos tutor o menor"
              className="app-input"
            />
            <input
              type="text"
              name="phone"
              value={filters.phone}
              onChange={handleChange}
              placeholder="Teléfono"
              className="app-input"
            />
            <input
              type="text"
              name="email"
              value={filters.email}
              onChange={handleChange}
              placeholder="Email"
              className="app-input"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              className="rounded-[22px] bg-[#0a84ff] text-white px-6 py-4 hover:bg-[#0077ed] transition font-medium shadow-[0_14px_24px_rgba(10,132,255,0.22)]"
            >
              Buscar
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="rounded-[22px] border border-[var(--app-border)] bg-[rgba(245,245,247,0.9)] px-6 py-4 font-medium text-slate-600 hover:bg-white transition"
            >
              Limpiar
            </button>
          </div>
        </form>
      </motion.div>

      {/* Results count */}
      <div className="px-2 flex items-center justify-between gap-4">
        <span className="text-sm text-slate-500">
          {loading
            ? 'Buscando...'
            : `${filteredClients.length} resultado${filteredClients.length !== 1 ? 's' : ''} · Página ${page + 1} de ${totalPages}`}
        </span>
        {hasPendingFilterChanges ? (
          <span className="text-sm text-amber-500">
            Pulsa Buscar para aplicar los cambios de filtro
          </span>
        ) : hasAppliedFilters ? (
          <span className="text-sm text-slate-400">
            Filtros aplicados sobre el listado actual
          </span>
        ) : null}
      </div>

      {/* Table */}
      <div className="rounded-[28px] border border-[var(--app-border)] bg-[var(--app-surface)] shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-[rgba(245,245,247,0.9)] text-slate-600">
              <tr>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Nombre</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Apellidos</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Email</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Teléfono</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Nacimiento</th>
                <th className="p-4 border-b border-[var(--app-border)] font-medium">Menores registrados</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-10 text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-slate-300 border-t-[var(--app-accent)] rounded-full animate-spin" />
                      Cargando...
                    </div>
                  </td>
                </tr>
              ) : paginatedClients.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-500">No hay resultados</td>
                </tr>
              ) : (
                paginatedClients.map((client) => (
                  <tr key={client.id} className="hover:bg-[rgba(245,245,247,0.45)] transition align-top">
                    <td className="p-4 border-b border-[var(--app-border)] font-medium text-slate-900">{client.name}</td>
                    <td className="p-4 border-b border-[var(--app-border)] text-slate-700">{client.fullSurname}</td>
                    <td className="p-4 border-b border-[var(--app-border)] text-slate-500">{client.email}</td>
                    <td className="p-4 border-b border-[var(--app-border)] text-slate-700">{client.phone}</td>
                    <td className="p-4 border-b border-[var(--app-border)] text-slate-500">{client.birthday}</td>
                    <td className="p-4 border-b border-[var(--app-border)]">
                      <div className="space-y-2">
                        {client.minors.map((minor, index) => (
                          <div
                            key={`${minor.fullName}-${index}`}
                            className="rounded-2xl bg-white border border-blue-100 px-3 py-2 text-sm text-slate-700"
                          >
                            <span className="font-medium text-slate-800">{minor.fullName}</span>
                            {minor.birthDate ? ` · ${minor.birthDate}` : ''}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          disabled={loading}
        />
      </div>
    </div>
  );
}
